import { CommonModule } from '@angular/common'
import { Component } from '@angular/core'

import {
    BatchStationEntryRow,
    BatchStationVarianceRow,
    DetailHeaderComponent,
    DetailHeaderModel,
    DetailKpiCard,
    DetailKpiRowComponent,
    DetailPage,
    DetailPageShellComponent,
    DetailSectionComponent,
    StationVarianceTableBatchComponent
} from '@shared/detail-page'

/**
 * Dev-only demo route that exercises every shared detail-page component.
 * Reachable at `/dev/detail-page-demo` — no auth.
 */
@DetailPage()
@Component({
    selector: 'app-detail-page-demo',
    standalone: true,
    imports: [
        CommonModule,
        DetailPageShellComponent,
        DetailSectionComponent,
        DetailHeaderComponent,
        DetailKpiRowComponent,
        StationVarianceTableBatchComponent
    ],
    templateUrl: './detail-page-demo.component.html'
})
export class DetailPageDemoComponent {
    readonly back = { route: ['/'] }

    readonly headerModel: DetailHeaderModel = {
        title: '305450010',
        status: { label: 'Running', tone: 'primary' },
        actions: [
            { id: 'edit', label: 'Edit', icon: 'fa-pencil', tone: 'secondary' },
            { id: 'complete', label: 'Complete', icon: 'fa-check', tone: 'primary' }
        ],
        chips: [
            { icon: 'fa-box', label: 'Edwards iH80 Pump', tooltip: 'Product' },
            { icon: 'fa-cube', label: 'Cell A · Station 3', tooltip: 'Equipment' },
            { icon: 'fa-clock', label: 'Started', value: 'Apr 24, 2:14 PM' },
            { icon: 'fa-stopwatch', label: 'Turnaround', value: '14h 32m' },
            { icon: 'fa-check-circle', label: 'Good', value: '12' }
        ]
    }

    readonly kpiCards: DetailKpiCard[] = [
        { id: 'touch', label: 'Total Touch', value: '8h 14m', sub: 'across 3 stations', subTone: 'muted' },
        { id: 'dwell', label: 'Total Dwell', value: '14h 32m', sub: 'wall-clock', subTone: 'muted' },
        { id: 'stations', label: 'Stations', value: '3', sub: 'Build · Test · QA', subTone: 'muted' },
        { id: 'operators', label: 'Operators', value: '4', sub: 'unique IDs', subTone: 'muted' },
        { id: 'efficiency', label: 'Efficiency', value: '89%', sub: 'std ÷ touch', subTone: 'over' }
    ]

    readonly varianceRows: BatchStationVarianceRow[] = [
        {
            station_id: 's-build',
            station_name: 'Build',
            touch_minutes: 240,
            dwell_minutes: 320,
            standard_minutes: 220,
            variance_ratio: 1.09,
            operators: [
                { user_id: 'u-1', name: 'Alex Reyes' },
                { user_id: 'u-2', name: 'Priya Patel' }
            ]
        },
        {
            station_id: 's-test',
            station_name: 'Test',
            touch_minutes: 165,
            dwell_minutes: 200,
            standard_minutes: 180,
            variance_ratio: 0.92,
            operators: [{ user_id: 'u-3', name: 'Sam Chen' }]
        },
        {
            station_id: 's-qa',
            station_name: 'QA',
            touch_minutes: 89,
            dwell_minutes: 130,
            standard_minutes: 60,
            variance_ratio: 1.48,
            operators: [{ user_id: 'u-4', name: 'Dana Cole' }]
        }
    ]

    readonly entriesByStation = new Map<string, BatchStationEntryRow[]>([
        ['s-build', [
            {
                time_entry_id: 'e-1',
                station_id: 's-build',
                user_id: 'u-1',
                user_name: 'Alex Reyes',
                start_ts: '2026-04-24T14:14:00Z',
                end_ts: '2026-04-24T18:30:00Z',
                duration_minutes: 256,
                is_open: false
            }
        ]],
        ['s-qa', [
            {
                time_entry_id: 'e-2',
                station_id: 's-qa',
                user_id: 'u-4',
                user_name: 'Dana Cole',
                start_ts: '2026-04-25T08:00:00Z',
                end_ts: null,
                duration_minutes: 89,
                is_open: true
            }
        ]]
    ])

    expandedStations = new Set<string>()

    onStationToggled(row: BatchStationVarianceRow): void {
        const key = row.station_id ?? '__null__'
        if (this.expandedStations.has(key)) {
            this.expandedStations.delete(key)
        } else {
            this.expandedStations.add(key)
        }
        this.expandedStations = new Set(this.expandedStations)
    }

    onEntryEditClicked(entry: BatchStationEntryRow): void {
        // eslint-disable-next-line no-console
        console.log('[demo] entry edit clicked', entry)
    }
}
